import { useState, useEffect } from "react";
import styled from "styled-components";
import BarcodeReader from "./BarcodeReader";
import UserSelector from "../UserComponents/UserSelector";
import BookService from "../../services/BookService";
import BorrowingService from "../../services/BorrowingService";
import Button from "../CommonComponents/Button";
import MessageModal from "../CommonComponents/MessageModal";
import { Container, Input, Label } from "../CommonComponents/DataCard";
import placeholderImage from "../../assets/logo.png";

const PageWrapper = styled.div`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.text};
`;

const Section = styled.div`
  margin-bottom: 2rem;
  background: ${({ theme }) => theme.colors.card};
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h3`
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text};
`;

const BookCard = styled.div`
  display: flex;
  gap: 1rem;
  background: ${({ theme }) => theme.colors.background};
  padding: 1rem;
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  margin: 1rem 0;
`;

const BookThumbnail = styled.img`
  width: 100px;
  height: 140px;
  object-fit: cover;
  border-radius: 6px;
`;

const BookInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  h4 {
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
  }

  p {
    margin: 0;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

function ReturnBook() {
    const [barcode, setBarcode] = useState("");
    const [book, setBook] = useState(null);
    const [thumbnail, setThumbnail] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState({
        book: false,
        user: false
    });

    const [modal, setModal] = useState({
        isOpen: false,
        title: "",
        message: "",
        variant: "success"
    });

    useEffect(() => {
        if (barcode.length > 0) {
            searchBookByBarcode(barcode);
        }
    }, [barcode]);

    const searchBookByBarcode = async (barcodeValue) => {
        try {
            setLoading(true);
            setError(prev => ({ ...prev, book: false }));

            const results = await BookService.searchBooks(barcodeValue, "barcode", 0, 1);

            // Check if results is a Page object with content array
            const books = results.content || results;

            if (books && books.length > 0) {
                const foundBook = books[0];
                setBook(foundBook);

                const thumbUrl = await BookService.getBookThumbnail(foundBook.id);
                setThumbnail(thumbUrl || placeholderImage);
            } else {
                setBook(null);
                setThumbnail(null);
                setError(prev => ({ ...prev, book: true }));
                setModal({
                    isOpen: true,
                    title: "Greška",
                    message: "Knjiga s ovim barkodom nije pronađena u sustavu.",
                    variant: "error"
                });
            }
        } catch (err) {
            console.error("Greška pri pretraživanju knjige:", err);
            setBook(null);
            setThumbnail(null);
            setError(prev => ({ ...prev, book: true }));
            setModal({
                isOpen: true,
                title: "Greška",
                message: "Greška pri pretraživanju knjige: " + err.message,
                variant: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleBarcodeChange = (value) => {
        setBarcode(value);
    };

    const handleSubmit = async () => {
        const newError = {
            book: !book,
            user: !selectedUser
        };

        setError(newError);

        if (newError.book || newError.user) {
            setModal({
                isOpen: true,
                title: "Nedostaju podaci",
                message: "Molimo skenirajte knjigu i odaberite korisnika prije vraćanja.",
                variant: "error"
            });
            return;
        }

        try {
            setLoading(true);
            await BorrowingService.returnBook(selectedUser.id, book.id);

            setModal({
                isOpen: true,
                title: "Uspjeh",
                message: `Knjiga "${book.name}" je uspješno vraćena od korisnika ${selectedUser.name} ${selectedUser.surname}.`,
                variant: "success"
            });

            setBarcode("");
            setBook(null);
            setThumbnail(null);
            setSelectedUser(null);
            setError({ book: false, user: false });

        } catch (err) {
            console.error("Greška pri vraćanju knjige:", err);
            setModal({
                isOpen: true,
                title: "Greška pri vraćanju",
                message: err.message || "Došlo je do greške pri vraćanju knjige.",
                variant: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => {
        setModal({ ...modal, isOpen: false });
    };

    return (
        <PageWrapper>
            <Title>Vraćanje knjige</Title>

            <Section>
                <SectionTitle>1. Skenirajte barkod knjige</SectionTitle>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
                    <BarcodeReader onScan={handleBarcodeChange} />
                </div>
                <Container>
                    <Label>Ili unesite barkod ručno:</Label>
                    <Input
                        type="text"
                        value={barcode}
                        onChange={(e) => handleBarcodeChange(e.target.value)}
                        placeholder="Unesite barkod knjige..."
                    />
                </Container>

                {loading && <p style={{ textAlign: "center" }}>Pretraživanje knjige...</p>}

                {book && (
                    <BookCard>
                        <BookThumbnail src={thumbnail || placeholderImage} alt={book.name} />
                        <BookInfo>
                            <h4>{book.name}</h4>
                            <p><strong>Žanr:</strong> {book.genre || "Nepoznato"}</p>
                            <p><strong>Autori:</strong> {book.authors?.map(a => `${a.name} ${a.surname}`).join(", ") || "Nepoznato"}</p>
                        </BookInfo>
                    </BookCard>
                )}
            </Section>

            <Section>
                <SectionTitle>2. Odaberite korisnika</SectionTitle>
                <UserSelector
                    selectedUser={selectedUser}
                    setSelectedUser={setSelectedUser}
                    error={error.user}
                />
            </Section>

            <ButtonContainer>
                <Button
                    tekst={loading ? "Vraćanje u tijeku..." : "Vrati knjigu"}
                    akcija={handleSubmit}
                    disabled={loading}
                    sirina="200px"
                />
            </ButtonContainer>

            <MessageModal
                isOpen={modal.isOpen}
                onClose={closeModal}
                title={modal.title}
                variant={modal.variant}
            >
                {modal.message}
            </MessageModal>
        </PageWrapper>
    );
}

export default ReturnBook;
